import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Spin, Tooltip, Typography } from 'antd'
import { CloseCircleOutlined, HomeOutlined, ReloadOutlined, ToolOutlined } from '@ant-design/icons'
import SimpleModal from '@/components/SimpleModal/Modal'
import { captureDevicePicture } from '@/service/ys7'
import {
  getMonitoringSceneFlags,
  isSevereMonitoringLevel
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'
import { enqueueYsDeviceCapture, parseYs7CapturePicUrl, isEzvizCaptureBrand } from '@/views/ys/share/ysDeviceCaptureUtils'

const { Text } = Typography

/** 预览区展示态；resolveThumbView 按此顺序自上而下命中 */
const THUMB_VIEW = {
  /** 基线未拉完且无告警：Spin 加载 */
  BASELINE_LOADING: 'baseline_loading',
  /** 有 monitoringAlarm.thumb：展示预警缩略图，可点放大 */
  ALERT_THUMB: 'alert_thumb',
  /** 无预警图但有列表缩略图：格子小图优先 thumbnailUrls */
  DEVICE_ALARM_IMAGE: 'device_alarm_image',
  /** 有告警但尚无图：文案「预警中」（可带快捷操作） */
  ALERT_PENDING: 'alert_pending',
  /** 萤石设备无告警、抓拍请求中：Spin + 刷新按钮 */
  CAPTURE_LOADING: 'capture_loading',
  /** 萤石云抓拍成功：展示 preview 截图 */
  CAPTURE_PREVIEW: 'capture_preview',
  /** list.status===0：设备离线，不打萤石抓拍 */
  OFFLINE: 'offline',
  /** 兜底：暂无截图 / 暂无预警画面 */
  EMPTY: 'empty'
}

/**
 * 单格预览展示态：自上而下命中即返回，与 THUMB_VIEW 优先级一致。
 * 有图优先（预警缩略图 → 列表预览图）；萤石抓拍仅手动刷新。
 */
function resolveThumbView ({
  monitoringBaselineReady,
  showAlertThumb,
  showDeviceAlarmImage,
  status,
  deviceIsAlarming,
  monitoringAlertActive,
  captureEligible,
  captureReady,
  loading,
  preview
}) {
  // ① list / SSE 预警缩略图（monitoringAlarm.thumb）
  if (showAlertThumb) return THUMB_VIEW.ALERT_THUMB
  // ② 无预警图时回退设备列表缩略图 thumbnailUrls（无则 imageUrls）
  if (showDeviceAlarmImage) return THUMB_VIEW.DEVICE_ALARM_IMAGE
  // ③ list.status=0：离线（有历史图时上面已命中）
  if (status === 0 || status === '0') return THUMB_VIEW.OFFLINE
  // ④ 设备列表基线未就绪：有告警先占位，否则转圈
  if (!monitoringBaselineReady) {
    if (deviceIsAlarming || monitoringAlertActive) return THUMB_VIEW.ALERT_PENDING
    return THUMB_VIEW.BASELINE_LOADING
  }
  // ⑤ 基线已就绪但告警仍无图：展示「预警画面加载中」占位（非抓拍）
  if (monitoringAlertActive || deviceIsAlarming) return THUMB_VIEW.ALERT_PENDING
  // ⑥ 无告警且为萤石设备：等待抓拍接口或 captureReady 条件满足
  if (captureEligible && (loading || !captureReady)) return THUMB_VIEW.CAPTURE_LOADING
  // ⑦ 萤石云抓拍成功，展示 preview
  if (captureEligible && preview) return THUMB_VIEW.CAPTURE_PREVIEW
  // ⑧ 非萤石或无可用画面
  return THUMB_VIEW.EMPTY
}

function joinThumbClass (...parts) {
  return parts.filter(Boolean).join(' ')
}

/**
 * 监控墙单格预览：优先展示监控预警（list 初始化 + SSE），与品牌无关；
 * 无预警图时回退列表缩略图；点开放大仍用大图。
 */
function SurveillanceCaptureThumb ({
  deviceSerial,
  channelNo,
  source,
  brand,
  /** monitoringdevice/list.status：1 在线 / 0 离线 */
  status,
  monitoringAlarm = null,
  monitoringBaselineReady,
  deviceLatestMonitoringImage = '',
  deviceLatestMonitoringImageThumbnail = '',
  deviceIsAlarming = false,
  /** 须已开启推理（且同步完成）才展示预警缩略图/警告框/快捷操作 */
  alarmDisplayEligible = false,
  onResolveNoBabyAlarm
}) {
  const ys7Token = useSelector((s) => s.app.ys7Token)
  const trustAccessToken = useSelector((s) => s.ys7.nvr.trustAccessToken)
  const nvrFetching = useSelector((s) => s.ys7.nvr.fetching)
  const [preview, setPreview] = useState('')
  const [loading, setLoading] = useState(false)
  const [preferCapture, setPreferCapture] = useState(false)
  const [refreshNonce, setRefreshNonce] = useState(0)

  const captureEligible = isEzvizCaptureBrand(brand) && status !== 0 && status !== '0'
  const monitoringAlertActive = Boolean(monitoringAlarm) && alarmDisplayEligible
  const effectiveDeviceIsAlarming = deviceIsAlarming && alarmDisplayEligible
  const seeData = monitoringAlertActive ? monitoringAlarm?.seeData : null
  const { hasBaby, isViolation } = getMonitoringSceneFlags(seeData)
  // 无婴儿：只认记录 hasBaby===false；增强墙不传回调 → 不展示快捷条
  const showNoBabyAlarmActions = monitoringAlertActive
    && typeof onResolveNoBabyAlarm === 'function'
    && hasBaby === false
  /** 仅有异常警告时展示错检；无告警（含仅列表历史图 / 普通抓拍）隐藏 */
  const showViolationAction =
    monitoringAlertActive &&
    typeof onResolveNoBabyAlarm === 'function' &&
    (isViolation === true || isSevereMonitoringLevel(seeData))
  const alertThumbUrl = monitoringAlertActive
    ? String(monitoringAlarm?.thumb ?? '').trim()
    : ''
  const alertFullImageUrl = monitoringAlertActive
    ? String(monitoringAlarm?.fullImage ?? '').trim()
    : ''
  const alertModalUrl = (alertFullImageUrl || alertThumbUrl).trim()
  const alertPreviewUrl = (alertThumbUrl || alertFullImageUrl).trim()
  const deviceListThumbUrl = String(deviceLatestMonitoringImageThumbnail ?? '').trim()
  const deviceListFullUrl = String(deviceLatestMonitoringImage ?? '').trim()
  // 格子小图用 thumbnailUrls；点开放大用 imageUrls
  const deviceListPreviewUrl = deviceListThumbUrl || deviceListFullUrl
  const showAlertThumb = alertModalUrl !== '' && !preferCapture
  const showDeviceAlarmImage =
    deviceListPreviewUrl !== '' && !showAlertThumb && !preferCapture
  const deviceAlarmModalUrl = deviceListFullUrl || deviceListPreviewUrl
  const effectiveAlertActive = monitoringAlertActive || effectiveDeviceIsAlarming
  const manualRefresh = refreshNonce > 0
  const captureReady =
    monitoringBaselineReady &&
    (!captureEligible || manualRefresh || !effectiveAlertActive)
  /** 后端列表已保证有图；初始化不打抓拍，仅手动刷新时调萤石云 */
  const shouldFetchCapture =
    captureEligible &&
    captureReady &&
    manualRefresh &&
    !effectiveAlertActive
  const viewMode = resolveThumbView({
    monitoringBaselineReady,
    showAlertThumb,
    showDeviceAlarmImage,
    status,
    deviceIsAlarming: effectiveDeviceIsAlarming,
    monitoringAlertActive,
    captureEligible,
    captureReady,
    loading,
    preview
  })
  const toolbarClass = joinThumbClass('surveillance-capture-thumb surveillance-capture-thumb--toolbar')
  const toolbarClassWithNb = joinThumbClass(
    toolbarClass,
    showNoBabyAlarmActions && 'surveillance-capture-thumb--nb-actions'
  )

  useEffect(() => {
    if (monitoringAlertActive) {
      setPreferCapture(false)
    }
  }, [monitoringAlertActive])

  useEffect(() => {
    if (!shouldFetchCapture) {
      if (!showAlertThumb && !captureEligible) {
        setPreview('')
        setLoading(false)
      }
      return undefined
    }

    const serial = String(deviceSerial).trim()
    const channelApi = Number(channelNo)

    let accessToken = ''
    if (source === 'trust') {
      if (nvrFetching && !trustAccessToken) {
        setPreview('')
        setLoading(true)
        return undefined
      }
      accessToken = trustAccessToken || ''
      if (!accessToken) {
        setPreview('')
        setLoading(false)
        return undefined
      }
    } else {
      accessToken = ys7Token || ''
      if (!accessToken) {
        setPreview('')
        setLoading(false)
        return undefined
      }
    }

    let cancelled = false
    setLoading(true)
    setPreview('')
    const task = () =>
      captureDevicePicture({
        accessToken,
        deviceSerial: serial,
        channelNo: channelApi
      })
        .then((res) => {
          if (!cancelled) setPreview(parseYs7CapturePicUrl(res) || '')
        })
        .catch(() => {
          if (!cancelled) setPreview('')
        })

    enqueueYsDeviceCapture(task).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [
    shouldFetchCapture,
    showAlertThumb,
    captureEligible,
    deviceSerial,
    channelNo,
    source,
    ys7Token,
    trustAccessToken,
    nvrFetching,
    refreshNonce
  ])

  const handleRefreshCapture = (e) => {
    if (!captureEligible) return
    e.stopPropagation()
    setPreferCapture(true)
    setRefreshNonce((n) => n + 1)
  }

  const renderRefreshBtn = () => {
    if (!captureEligible || effectiveAlertActive) return null
    return (
      <Tooltip title="刷新截图" mouseEnterDelay={0.3}>
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          className="surveillance-capture-thumb__refresh"
          disabled={!monitoringBaselineReady}
          loading={loading && preferCapture}
          aria-label="刷新截图"
          onClick={handleRefreshCapture}
        />
      </Tooltip>
    )
  }

  const handleAlarmAction = (kind) => (e) => {
    e.stopPropagation()
    const alertId = monitoringAlarm?.recordId ?? seeData?.id
    if (typeof onResolveNoBabyAlarm === 'function') {
      onResolveNoBabyAlarm(kind, alertId)
    }
  }

  const renderViolationActionBtn = () => {
    // 没有异常警告时隐藏错检（与 showViolationAction 一致，显式兜底）
    if (!monitoringAlertActive || !showViolationAction) return null
    return (
      <Tooltip title="标记为错检" mouseEnterDelay={0.3}>
        <Button
          type="text"
          size="small"
          icon={<CloseCircleOutlined />}
          className={joinThumbClass(
            'surveillance-capture-thumb__wrong-check',
            showNoBabyAlarmActions && 'surveillance-capture-thumb__wrong-check--with-nb-panel'
          )}
          aria-label="错检"
          onClick={handleAlarmAction('wrong_check')}
        >
          错检
        </Button>
      </Tooltip>
    )
  }

  const renderAlarmOverlayActions = () => {
    if (!showNoBabyAlarmActions) return null
    return (
      <div
        className="surveillance-capture-thumb__overlay-actions"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <p className="surveillance-capture-thumb__nb-hint">宝宝不在床上，请登记去哪了</p>
        <div className="surveillance-capture-thumb__nb-btn-row">
          <Button
            type="primary"
            size="large"
            icon={<ToolOutlined />}
            className="surveillance-capture-thumb__nb-btn surveillance-capture-thumb__nb-btn--operate"
            onClick={handleAlarmAction('operate')}
          >
            操作
          </Button>
          <Button
            size="large"
            icon={<HomeOutlined />}
            className="surveillance-capture-thumb__nb-btn surveillance-capture-thumb__nb-btn--return"
            onClick={handleAlarmAction('return_room')}
          >
            回房
          </Button>
        </div>
      </div>
    )
  }

  switch (viewMode) {
    case THUMB_VIEW.BASELINE_LOADING:
      return (
        <div className={joinThumbClass('surveillance-capture-thumb--loading', toolbarClass)}>
          <Spin size="small" />
        </div>
      )

    case THUMB_VIEW.ALERT_THUMB: {
      const thumbUrl = alertPreviewUrl
      return (
        <div className={toolbarClassWithNb}>
          {renderRefreshBtn()}
          {renderViolationActionBtn()}
          <SimpleModal baseProps={{ url: alertModalUrl }}>
            <img
              key={thumbUrl}
              src={thumbUrl}
              alt=""
              className="surveillance-capture-thumb__img surveillance-capture-thumb__img--zoomable"
              loading="lazy"
              decoding="async"
            />
          </SimpleModal>
          {renderAlarmOverlayActions()}
        </div>
      )
    }

    case THUMB_VIEW.DEVICE_ALARM_IMAGE: {
      const thumbUrl = deviceListPreviewUrl
      const modalUrl = deviceAlarmModalUrl
      return (
        <div className={toolbarClass}>
          {renderRefreshBtn()}
          <SimpleModal baseProps={{ url: modalUrl }}>
            <img
              key={thumbUrl}
              src={thumbUrl}
              alt=""
              className="surveillance-capture-thumb__img surveillance-capture-thumb__img--zoomable"
              loading="lazy"
              decoding="async"
            />
          </SimpleModal>
        </div>
      )
    }

    case THUMB_VIEW.ALERT_PENDING:
      return (
        <div
          className={joinThumbClass(
            'surveillance-capture-thumb--empty',
            toolbarClass,
            'surveillance-capture-thumb--monitoring-alert',
            showNoBabyAlarmActions && 'surveillance-capture-thumb--nb-actions'
          )}
        >
          {renderViolationActionBtn()}
          <Text type="secondary" className="surveillance-capture-thumb__empty-text">
            预警中
          </Text>
          {renderAlarmOverlayActions()}
        </div>
      )

    case THUMB_VIEW.CAPTURE_LOADING:
      return (
        <div className={joinThumbClass('surveillance-capture-thumb--loading', toolbarClass)}>
          {renderRefreshBtn()}
          <Spin size="small" />
        </div>
      )

    case THUMB_VIEW.CAPTURE_PREVIEW:
      return (
        <div className={toolbarClass}>
          {renderRefreshBtn()}
          <SimpleModal baseProps={{ url: preview }}>
            <img
              src={preview}
              alt=""
              className="surveillance-capture-thumb__img surveillance-capture-thumb__img--zoomable"
              loading="lazy"
              decoding="async"
            />
          </SimpleModal>
        </div>
      )

    case THUMB_VIEW.OFFLINE:
      return (
        <div className={joinThumbClass('surveillance-capture-thumb--empty', toolbarClass)}>
          <Text type="secondary" className="surveillance-capture-thumb__empty-text">
            设备离线
          </Text>
        </div>
      )

    case THUMB_VIEW.EMPTY:
    default:
      return (
        <div className={joinThumbClass('surveillance-capture-thumb--empty', toolbarClass)}>
          {renderRefreshBtn()}
          <Text type="secondary" className="surveillance-capture-thumb__empty-text">
            {captureEligible ? '暂无截图' : '暂无预警画面'}
          </Text>
        </div>
      )
  }
}

export default memo(SurveillanceCaptureThumb)
